/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global Blob, ClipboardItem, document, localStorage, Office, Word, fetch, Headers, HTMLButtonElement, HTMLCanvasElement, HTMLImageElement, HTMLInputElement, HTMLSelectElement, Response, HTMLElement, HTMLTextAreaElement, URL, navigator, window */

import { buildApiUrl, getTaskpaneConfig } from "./config";

type RewriteAction = "improve" | "clarify" | "commercial" | "answer";
type ReferenceMode = "priority" | "strict" | "mixed";
type AnswerLevel = "short" | "structured" | "detailed";

interface RewriteResponse {
  result?: string;
  error?: string;
  references?: ReferenceSummary[];
  visuals?: VisualSummary[];
  totalVisualsAvailable?: number;
  referenceMode?: ReferenceMode;
  answerLevel?: AnswerLevel;
  usedInternalReferences?: boolean;
}

interface ReferenceSummary {
  id: string;
  packId?: string;
  title: string;
  category: string;
  path: string;
  score: number;
}

interface VisualSummary {
  id: string;
  packId?: string;
  referenceId: string;
  referenceTitle: string;
  path: string;
  url?: string;
  filename: string;
  caption: string;
  isScreenshotCandidate: boolean;
}

const taskpaneConfig = getTaskpaneConfig();
const API_URL = buildApiUrl("/api/rewrite");
const EMPTY_SELECTION_MESSAGE = "Sélectionne d’abord un passage dans Word.";
const ACCESS_TOKEN_STORAGE_KEY = "odps-assistant.accessToken";

let isBusy = false;
let visualObjectUrls: string[] = [];

Office.onReady((info) => {
  if (info.host !== Office.HostType.Word) {
    return;
  }

  const sideloadMessage = document.getElementById("sideload-msg");
  const appBody = document.getElementById("app-body");

  if (sideloadMessage) {
    sideloadMessage.style.display = "none";
  }

  if (appBody) {
    appBody.style.display = "flex";
  }

  getElement<HTMLButtonElement>("improve-button").onclick = () => rewriteSelection("improve");
  getElement<HTMLButtonElement>("clarify-button").onclick = () => rewriteSelection("clarify");
  getElement<HTMLButtonElement>("commercial-button").onclick = () => rewriteSelection("commercial");
  getElement<HTMLButtonElement>("answer-button").onclick = () => rewriteSelection("answer");
  getElement<HTMLButtonElement>("replace-button").onclick = replaceSelectionInWord;
  getElement<HTMLButtonElement>("insert-after-button").onclick = insertAfterSelectionInWord;
  getElement<HTMLButtonElement>("save-access-token-button").onclick = saveAccessToken;
  getElement<HTMLButtonElement>("edit-access-token-button").onclick = () =>
    showAccessKeyPanel(true);
  getElement<HTMLButtonElement>("open-access-key-panel-button").onclick = () =>
    showAccessKeyPanel(true);

  updateAccessKeyPanel();
});

async function rewriteSelection(action: RewriteAction): Promise<void> {
  if (isBusy) {
    return;
  }

  clearMessages();
  if (action === "answer") {
    showReferenceMessage("Recherche dans le référentiel...");
    showVisualMessage("Recherche de visuels suggérés...");
  } else {
    clearReferenceDisplay();
    clearVisualDisplay();
  }

  setBusy(true, action === "answer" ? "Réponse en cours..." : "Transformation en cours...");

  try {
    const selectedText = await readSelectedText();

    if (!selectedText) {
      showError(EMPTY_SELECTION_MESSAGE);
      return;
    }

    const contextText = action === "answer" ? getContextTextArea().value.trim() : "";
    const response = await requestRewrite(action, selectedText, contextText || undefined);
    getResultTextArea().value = response.result;

    if (action === "answer") {
      updateReferenceDisplay(response.references || [], response.usedInternalReferences === true);
      updateVisualDisplay(response.visuals || [], response.totalVisualsAvailable || 0);
    }

    showStatus(
      action === "answer"
        ? "Réponse générée. Clique sur Insérer après la sélection pour l’ajouter au document."
        : "Résultat prêt."
    );
  } catch (error) {
    showError(getErrorMessage(error, "La transformation a échoué."));
  } finally {
    setBusy(false);
  }
}

async function replaceSelectionInWord(): Promise<void> {
  if (isBusy) {
    return;
  }

  clearMessages();

  const replacementText = getResultTextArea().value.trim();

  if (!replacementText) {
    showError("La zone de résultat est vide.");
    return;
  }

  setBusy(true, "Remplacement en cours...");

  try {
    const selectedText = await readSelectedText();

    if (!selectedText) {
      showError(EMPTY_SELECTION_MESSAGE);
      return;
    }

    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.insertText(replacementText, Word.InsertLocation.replace);
      await context.sync();
    });

    showStatus("Texte remplacé dans Word.");
  } catch (error) {
    showError(getErrorMessage(error, "Le remplacement dans Word a échoué."));
  } finally {
    setBusy(false);
  }
}

async function insertAfterSelectionInWord(): Promise<void> {
  if (isBusy) {
    return;
  }

  clearMessages();

  const textToInsert = getResultTextArea().value.trim();

  if (!textToInsert) {
    showError("La zone de résultat est vide.");
    return;
  }

  setBusy(true, "Insertion en cours...");

  try {
    const selectedText = await readSelectedText();

    if (!selectedText) {
      showError(EMPTY_SELECTION_MESSAGE);
      return;
    }

    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.insertText(`\n\n${textToInsert}`, Word.InsertLocation.after);
      await context.sync();
    });

    showStatus("Réponse insérée après la sélection.");
  } catch (error) {
    showError(getErrorMessage(error, "L’insertion dans Word a échoué."));
  } finally {
    setBusy(false);
  }
}

async function readSelectedText(): Promise<string> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("text");
    await context.sync();

    return selection.text.trim();
  });
}

async function requestRewrite(
  action: RewriteAction,
  text: string,
  contextText?: string
): Promise<{
  result: string;
  references?: ReferenceSummary[];
  visuals?: VisualSummary[];
  totalVisualsAvailable?: number;
  usedInternalReferences?: boolean;
}> {
  const payload: {
    action: RewriteAction;
    text: string;
    context?: string;
    referenceMode?: ReferenceMode;
    answerLevel?: AnswerLevel;
  } = {
    action,
    text,
  };

  if (contextText) {
    payload.context = contextText;
  }

  if (action === "answer") {
    payload.referenceMode = getReferenceMode();
    payload.answerLevel = getAnswerLevel();
  }

  const response = await fetch(API_URL, {
    method: "POST",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  });

  const responsePayload = await parseJsonResponse(response);

  if (!response.ok) {
    if (response.status === 401) {
      showAccessKeyPanel(true);
    }

    if (action === "answer") {
      updateReferenceDisplay(
        responsePayload.references || [],
        responsePayload.usedInternalReferences === true
      );
      updateVisualDisplay(
        responsePayload.visuals || [],
        responsePayload.totalVisualsAvailable || 0
      );
    }

    throw new Error(responsePayload.error || "Le backend local a refusé la demande.");
  }

  if (!responsePayload.result) {
    throw new Error("Le backend local n’a pas retourné de résultat.");
  }

  return {
    result: responsePayload.result,
    references: responsePayload.references,
    visuals: responsePayload.visuals,
    totalVisualsAvailable: responsePayload.totalVisualsAvailable,
    usedInternalReferences: responsePayload.usedInternalReferences,
  };
}

async function parseJsonResponse(response: Response): Promise<RewriteResponse> {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function setBusy(nextBusy: boolean, statusMessage = ""): void {
  isBusy = nextBusy;

  getElement<HTMLButtonElement>("improve-button").disabled = nextBusy;
  getElement<HTMLButtonElement>("clarify-button").disabled = nextBusy;
  getElement<HTMLButtonElement>("commercial-button").disabled = nextBusy;
  getElement<HTMLButtonElement>("answer-button").disabled = nextBusy;
  getElement<HTMLButtonElement>("replace-button").disabled = nextBusy;
  getElement<HTMLButtonElement>("insert-after-button").disabled = nextBusy;

  if (statusMessage) {
    showStatus(statusMessage);
  }
}

function clearMessages(): void {
  showStatus("");
  showError("");
}

function showStatus(message: string): void {
  getElement<HTMLElement>("status-message").textContent = message;
}

function showError(message: string): void {
  getElement<HTMLElement>("error-message").textContent = message;
}

function getResultTextArea(): HTMLTextAreaElement {
  return getElement<HTMLTextAreaElement>("result-text");
}

function getContextTextArea(): HTMLTextAreaElement {
  return getElement<HTMLTextAreaElement>("context-text");
}

function getReferenceMode(): ReferenceMode {
  return getElement<HTMLSelectElement>("reference-mode").value as ReferenceMode;
}

function getAnswerLevel(): AnswerLevel {
  return getElement<HTMLSelectElement>("answer-level").value as AnswerLevel;
}

function updateReferenceDisplay(
  references: ReferenceSummary[],
  usedInternalReferences: boolean
): void {
  const container = getReferenceListElement();
  container.textContent = "";

  if (!usedInternalReferences || references.length === 0) {
    container.textContent = "Aucun référentiel interne pertinent trouvé.";
    return;
  }

  const list = document.createElement("ul");
  list.className = "reference-items";

  for (const reference of references) {
    const item = document.createElement("li");
    item.textContent = reference.title;
    list.appendChild(item);
  }

  container.appendChild(list);
}

function showReferenceMessage(message: string): void {
  getReferenceListElement().textContent = message;
}

function clearReferenceDisplay(): void {
  getReferenceListElement().textContent = "";
}

function getReferenceListElement(): HTMLElement {
  return getElement<HTMLElement>("reference-list");
}

function updateVisualDisplay(visuals: VisualSummary[], totalVisualsAvailable: number): void {
  const container = getVisualListElement();
  revokeVisualObjectUrls();
  container.textContent = "";

  if (visuals.length === 0) {
    container.textContent = "Aucun visuel suggéré pour les références utilisées.";
    return;
  }

  const list = document.createElement("div");
  list.className = "visual-items";

  if (totalVisualsAvailable > visuals.length) {
    const countMessage = document.createElement("p");
    countMessage.className = "visual-count";
    countMessage.textContent = `${visuals.length} visuels affichés sur ${totalVisualsAvailable} disponibles.`;
    list.appendChild(countMessage);
  }

  for (const visual of visuals) {
    const card = document.createElement("article");
    card.className = "visual-card";

    const thumbnail = document.createElement("img") as HTMLImageElement;
    thumbnail.className = "visual-thumb";
    thumbnail.alt = visual.caption || visual.filename;
    loadVisualThumbnail(thumbnail, visual);

    const meta = document.createElement("div");
    meta.className = "visual-meta";

    const name = document.createElement("p");
    name.className = "visual-name";
    name.textContent = visual.filename;

    const source = document.createElement("p");
    source.className = "visual-source";
    source.textContent = visual.referenceTitle;

    const actions = document.createElement("div");
    actions.className = "visual-actions";

    const copyImageButton = document.createElement("button");
    copyImageButton.className = "visual-copy-button visual-copy-image-button";
    copyImageButton.type = "button";
    copyImageButton.textContent = "Copier l’image";
    copyImageButton.setAttribute("aria-label", `Copier l’image ${visual.filename}`);
    copyImageButton.onclick = () => copyVisualImage(visual);

    const openButton = document.createElement("button");
    openButton.className = "visual-action-link";
    openButton.type = "button";
    openButton.textContent = "Ouvrir";
    openButton.setAttribute("aria-label", `Ouvrir le visuel ${visual.filename}`);
    openButton.onclick = () => openVisual(visual);

    const copyButton = document.createElement("button");
    copyButton.className = "visual-copy-button";
    copyButton.type = "button";
    copyButton.textContent = "Copier le chemin";
    copyButton.setAttribute("aria-label", `Copier le chemin du visuel ${visual.filename}`);
    copyButton.onclick = () => copyVisualPath(visual);

    actions.appendChild(copyImageButton);
    actions.appendChild(openButton);
    actions.appendChild(copyButton);
    meta.appendChild(name);
    meta.appendChild(source);
    meta.appendChild(actions);
    card.appendChild(thumbnail);
    card.appendChild(meta);
    list.appendChild(card);
  }

  container.appendChild(list);
}

function showVisualMessage(message: string): void {
  getVisualListElement().textContent = message;
}

function clearVisualDisplay(): void {
  revokeVisualObjectUrls();
  getVisualListElement().textContent = "";
}

function getVisualListElement(): HTMLElement {
  return getElement<HTMLElement>("visual-list");
}

function revokeVisualObjectUrls(): void {
  for (const objectUrl of visualObjectUrls) {
    URL.revokeObjectURL(objectUrl);
  }

  visualObjectUrls = [];
}

function getVisualUrl(visual: VisualSummary): string {
  if (visual.url) {
    return visual.url;
  }

  return buildApiUrl(`/api/visuals/${encodeURIComponent(visual.id)}`);
}

async function loadVisualThumbnail(
  imageElement: HTMLImageElement,
  visual: VisualSummary
): Promise<void> {
  try {
    imageElement.src = await createVisualObjectUrl(visual);
  } catch {
    imageElement.alt = `Miniature indisponible: ${visual.filename}`;
  }
}

async function openVisual(visual: VisualSummary): Promise<void> {
  try {
    const objectUrl = await createVisualObjectUrl(visual);
    window.open(objectUrl, "_blank", "noreferrer");
  } catch {
    await copyVisualPath(visual, {
      successMessage: "Le visuel n’a pas pu être ouvert. Le chemin du visuel a été copié.",
      errorMessage: "Impossible d’ouvrir le visuel ou de copier son chemin.",
    });
  }
}

async function createVisualObjectUrl(visual: VisualSummary): Promise<string> {
  const blob = await fetchVisualBlob(visual);
  const objectUrl = URL.createObjectURL(blob);
  visualObjectUrls.push(objectUrl);

  return objectUrl;
}

async function copyVisualImage(visual: VisualSummary): Promise<void> {
  try {
    const blob = await fetchVisualBlob(visual);
    const pngBlob = blob.type === "image/png" ? blob : await convertImageBlobToPng(blob);

    if (!navigator.clipboard || typeof navigator.clipboard.write !== "function") {
      throw new Error("Clipboard image non disponible.");
    }

    await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
    showStatus("Image copiée. Colle-la dans Word à l’emplacement souhaité.");
  } catch {
    await copyVisualPath(visual, {
      successMessage:
        "La copie directe de l’image n’est pas disponible dans ce contexte. Le chemin du visuel a été copié.",
      errorMessage:
        "La copie directe de l’image n’est pas disponible et le chemin n’a pas pu être copié.",
    });
  }
}

async function fetchVisualBlob(visual: VisualSummary): Promise<Blob> {
  const response = await fetch(getVisualUrl(visual), {
    headers: withAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      showAccessKeyPanel(true);
    }

    throw new Error("Image introuvable.");
  }

  return response.blob();
}

async function convertImageBlobToPng(blob: Blob): Promise<Blob> {
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await loadImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Canvas indisponible.");
    }

    context.drawImage(image, 0, 0);

    return await canvasToPngBlob(canvas);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(sourceUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = document.createElement("img") as HTMLImageElement;
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Chargement de l’image impossible."));
    image.src = sourceUrl;
  });
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }

      reject(new Error("Conversion PNG impossible."));
    }, "image/png");
  });
}

async function copyVisualPath(
  visual: VisualSummary,
  messages: { successMessage: string; errorMessage: string } = {
    successMessage: "Chemin du visuel copié.",
    errorMessage: "Impossible de copier le chemin du visuel.",
  }
): Promise<void> {
  try {
    await copyTextToClipboard(visual.url || visual.path);
    showStatus(messages.successMessage);
  } catch {
    showError(messages.errorMessage);
  }
}

async function copyTextToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  copyTextFallback(value);
}

function copyTextFallback(value: string): void {
  const temporaryTextArea = document.createElement("textarea");
  temporaryTextArea.value = value;
  temporaryTextArea.setAttribute("readonly", "true");
  temporaryTextArea.style.position = "fixed";
  temporaryTextArea.style.left = "-9999px";
  document.body.appendChild(temporaryTextArea);
  temporaryTextArea.select();
  document.execCommand("copy");
  document.body.removeChild(temporaryTextArea);
}

function withAuthHeaders(initialHeaders: Record<string, string> = {}): Headers {
  const headers = new Headers(initialHeaders);
  const accessToken = getStoredAccessToken();

  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  return headers;
}

function getStoredAccessToken(): string {
  try {
    return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

function saveAccessToken(): void {
  const input = getElement<HTMLInputElement>("access-token");
  const token = input.value.trim();

  if (!token) {
    showError("Renseigne la clé d’accès.");
    showAccessKeyPanel(true);
    return;
  }

  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
  input.value = "";
  clearMessages();
  showStatus("Clé d’accès enregistrée.");
  updateAccessKeyPanel();
}

function showAccessKeyPanel(forceOpen = false): void {
  const panel = getElement<HTMLElement>("access-key-panel");
  panel.hidden = false;

  if (forceOpen) {
    setAccessKeyFormVisible(true);
    updateAccessKeyFooter(false);
    getElement<HTMLElement>("access-key-status").textContent = "Saisis ou remplace la clé d’accès.";
    return;
  }

  updateAccessKeyPanel();
}

function updateAccessKeyPanel(): void {
  const panel = getElement<HTMLElement>("access-key-panel");
  const hasToken = getStoredAccessToken().length > 0;

  panel.hidden = hasToken || (!taskpaneConfig.isProduction && !hasToken);
  setAccessKeyFormVisible(!hasToken);
  updateAccessKeyFooter(hasToken);
  getElement<HTMLElement>("access-key-status").textContent = hasToken
    ? ""
    : "Saisis la clé fournie pour le pilote privé.";
}

function setAccessKeyFormVisible(isVisible: boolean): void {
  getElement<HTMLElement>("access-key-form").hidden = !isVisible;
  getElement<HTMLButtonElement>("edit-access-token-button").hidden = isVisible;
}

function updateAccessKeyFooter(isVisible: boolean): void {
  getElement<HTMLElement>("access-key-footer").hidden = !isVisible;
}

function getElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);

  if (!element) {
    throw new Error(`Élément introuvable: ${id}`);
  }

  return element as T;
}

function getErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
}
